import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ClusterSuggestion } from '../api'

export type FacesViewMode = 'all' | 'grouped' | 'deleted'

interface FacesState {
  // Selection
  selectedFaceIds: string[]
  isSelecting: boolean
  lastSelectedIndex: number | null

  // View mode
  viewMode: FacesViewMode

  // Clustering suggestions (ephemeral)
  clusterSuggestions: ClusterSuggestion[]
  isClustering: boolean

  // Inline naming
  editingFaceId: string | null

  // Face profile
  profileFaceId: string | null

  // Person profile
  profilePersonId: string | null
}

const initialState: FacesState = {
  selectedFaceIds: [],
  isSelecting: false,
  lastSelectedIndex: null,

  viewMode: 'all',

  clusterSuggestions: [],
  isClustering: false,

  editingFaceId: null,

  profileFaceId: null,
  profilePersonId: null,
}

const facesSlice = createSlice({
  name: 'faces',
  initialState,
  reducers: {
    // Selection
    toggleSelectMode: (state) => {
      state.isSelecting = !state.isSelecting
      if (!state.isSelecting) {
        state.selectedFaceIds = []
        state.lastSelectedIndex = null
      }
    },

    toggleFaceSelection: (state, action: PayloadAction<{ faceId: string; index: number }>) => {
      const { faceId, index } = action.payload
      const idx = state.selectedFaceIds.indexOf(faceId)
      if (idx >= 0) {
        state.selectedFaceIds.splice(idx, 1)
      } else {
        state.selectedFaceIds.push(faceId)
      }
      state.lastSelectedIndex = index
    },

    selectFaceRange: (
      state,
      action: PayloadAction<{ faceIds: string[]; fromIndex: number; toIndex: number }>
    ) => {
      const { faceIds, fromIndex, toIndex } = action.payload
      const start = Math.min(fromIndex, toIndex)
      const end = Math.max(fromIndex, toIndex)
      const rangeIds = faceIds.slice(start, end + 1)
      // Add all in range that aren't already selected
      for (const id of rangeIds) {
        if (!state.selectedFaceIds.includes(id)) {
          state.selectedFaceIds.push(id)
        }
      }
      state.lastSelectedIndex = toIndex
    },

    clearFaceSelection: (state) => {
      state.selectedFaceIds = []
      state.lastSelectedIndex = null
    },

    // View mode
    setViewMode: (state, action: PayloadAction<FacesViewMode>) => {
      state.viewMode = action.payload
      // Clear selection when switching views
      state.selectedFaceIds = []
      state.lastSelectedIndex = null
    },

    // Clustering
    setClusterSuggestions: (state, action: PayloadAction<ClusterSuggestion[]>) => {
      state.clusterSuggestions = action.payload
      state.isClustering = false
    },

    setIsClustering: (state, action: PayloadAction<boolean>) => {
      state.isClustering = action.payload
    },

    removeSuggestion: (state, action: PayloadAction<string>) => {
      state.clusterSuggestions = state.clusterSuggestions.filter(
        (s) => s.suggestion_id !== action.payload
      )
    },

    clearSuggestions: (state) => {
      state.clusterSuggestions = []
    },

    // Inline naming
    setEditingFaceId: (state, action: PayloadAction<string | null>) => {
      state.editingFaceId = action.payload
    },

    // Face profile (mutual exclusion with person profile)
    openFaceProfile: (state, action: PayloadAction<string>) => {
      state.profileFaceId = action.payload
      state.profilePersonId = null
    },
    closeFaceProfile: (state) => {
      state.profileFaceId = null
    },

    // Person profile (mutual exclusion with face profile)
    openPersonProfile: (state, action: PayloadAction<string>) => {
      state.profilePersonId = action.payload
      state.profileFaceId = null
    },
    closePersonProfile: (state) => {
      state.profilePersonId = null
    },
  },
})

export const {
  toggleSelectMode,
  toggleFaceSelection,
  selectFaceRange,
  clearFaceSelection,
  setViewMode,
  setClusterSuggestions,
  setIsClustering,
  removeSuggestion,
  clearSuggestions,
  setEditingFaceId,
  openFaceProfile,
  closeFaceProfile,
  openPersonProfile,
  closePersonProfile,
} = facesSlice.actions

export default facesSlice.reducer
